import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "../lib/api";
import { MapPin, Globe, Linkedin, Instagram, Phone, Mail, ArrowLeft, Calendar } from "lucide-react";
import VideoPlayer from "../components/VideoPlayer";

function ProgressBar({ raised, goal }) {
  const pct = Math.min(100, Math.max(0, (raised / (goal || 1)) * 100));
  return (
    <div>
      <div className="flex justify-between text-sm uppercase tracking-widest text-bdu-cream/60 mb-2">
        <span>${Number(raised || 0).toLocaleString()} raised</span>
        <span>${Number(goal || 0).toLocaleString()} goal</span>
      </div>
      <div className="h-3 bg-bdu-surface2 border border-bdu-gold/20 overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-bdu-gold to-amber-300 transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="mt-2 text-right text-sm text-bdu-gold">{pct.toFixed(1)}% funded</div>
    </div>
  );
}

export default function FounderDetail() {
  const { id } = useParams();
  const [f, setF] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/founders/${id}`).then((r) => setF(r.data)).catch((e) => setError(e?.response?.data?.detail || "Not found"));
  }, [id]);

  if (error) {
    return (
      <div className="pt-32 max-w-3xl mx-auto px-6 text-center">
        <div className="font-serif text-3xl text-bdu-cream">Founder Not Found</div>
        <p className="mt-4 text-bdu-cream/60">{error}</p>
        <Link to="/founders" className="btn-gold mt-8 inline-flex">Back to Founders</Link>
      </div>
    );
  }
  if (!f) return <div className="pt-32 text-center text-bdu-cream/50">Loading…</div>;

  return (
    <div className="pt-16">
      <section className="max-w-6xl mx-auto px-6 sm:px-10 pt-8 pb-20">
        <Link to="/founders" className="inline-flex items-center gap-2 text-bdu-cream/60 hover:text-bdu-gold text-sm uppercase tracking-widest mb-8" data-testid="founder-back-btn">
          <ArrowLeft size={14} /> Back to Founders Circle
        </Link>

        <div className="grid lg:grid-cols-12 gap-10">
          {/* Left: Video + updates */}
          <div className="lg:col-span-8">
            <VideoPlayer url={f.video_url} posterUrl={f.photo_url} testId="founder-detail-video" />

            <div className="mt-10">
              <div className="label-eyebrow" style={{ color: "rgba(212,175,55,0.9)" }}>{f.category} · {f.city}, {f.state}</div>
              <h1 className="mt-4 font-serif text-5xl text-bdu-cream leading-[1.05] tracking-tight">
                {f.business_name}
              </h1>
              <div className="mt-2 text-bdu-cream/60">by <span className="text-bdu-cream">{f.name}</span>{f.age ? `, age ${f.age}` : ""}</div>
              {f.tagline && <div className="mt-4 font-serif italic text-xl text-bdu-gold/90">{f.tagline}</div>}

              <p className="mt-8 text-bdu-cream/80 whitespace-pre-wrap leading-relaxed">{f.description}</p>
            </div>

            {/* Updates timeline */}
            {f.updates && f.updates.length > 0 && (
              <div className="mt-16">
                <h2 className="font-serif text-3xl text-bdu-cream mb-6">Journey Updates</h2>
                <div className="space-y-6">
                  {f.updates.map((u) => (
                    <div key={u.id} className="border-l-2 border-bdu-gold/40 pl-6" data-testid={`founder-update-${u.id}`}>
                      <div className="flex items-center gap-2 text-xs text-bdu-cream/50 uppercase tracking-widest">
                        <Calendar size={12} /> {new Date(u.created_at).toLocaleDateString()}
                      </div>
                      <h3 className="mt-2 font-serif text-xl text-bdu-cream">{u.title}</h3>
                      <p className="mt-2 text-bdu-cream/70 whitespace-pre-wrap">{u.body}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right: Fund + contact card */}
          <aside className="lg:col-span-4">
            <div className="bdu-card sticky top-24">
              <div className="label-eyebrow">Funding Goal</div>
              <div className="mt-6">
                <ProgressBar raised={f.raised_dollars} goal={f.goal_dollars} />
              </div>
              <div className="mt-8 space-y-2 text-sm">
                <Link to="/pledge" className="btn-gold w-full justify-center" data-testid="founder-donate-btn">
                  Donate to Fund This Vision
                </Link>
                <a
                  href="https://www.zeffy.com/en-US/ticketing/black-dollars-united-corporations-memberships"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="founder-zeffy-btn"
                  className="inline-flex items-center gap-2 w-full justify-center px-5 py-3 uppercase tracking-widest text-xs font-bold text-black transition-all hover:brightness-110"
                  style={{
                    background: "linear-gradient(135deg, #E8E8E8 0%, #C0C0C0 50%, #A8A8A8 100%)",
                    border: "1px solid #B0B0B0",
                  }}
                >
                  Donate Fee-Free via Zeffy
                </a>
              </div>
              <div className="mt-8 pt-6 border-t border-bdu-gold/15 space-y-3 text-sm">
                {f.website && (
                  <a href={f.website.startsWith("http") ? f.website : `https://${f.website}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-bdu-cream/70 hover:text-bdu-gold">
                    <Globe size={16} /> {f.website}
                  </a>
                )}
                {f.linkedin && (
                  <a href={f.linkedin} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-bdu-cream/70 hover:text-bdu-gold">
                    <Linkedin size={16} /> LinkedIn
                  </a>
                )}
                {f.instagram && (
                  <a href={f.instagram} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-bdu-cream/70 hover:text-bdu-gold">
                    <Instagram size={16} /> Instagram
                  </a>
                )}
                {f.email && (
                  <div className="flex items-center gap-3 text-bdu-cream/70"><Mail size={16} /> {f.email}</div>
                )}
                {f.phone && (
                  <div className="flex items-center gap-3 text-bdu-cream/70"><Phone size={16} /> {f.phone}</div>
                )}
                <div className="flex items-center gap-3 text-bdu-cream/50">
                  <MapPin size={16} /> {f.city}, {f.state}
                </div>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
